import React, { useState } from 'react';
import { Crown, Clock, Users, MonitorUp, X, Zap, AlertCircle } from 'lucide-react';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (tier: 'pro' | 'enterprise') => Promise<void>;
  isHost: boolean;
  currentTier: 'free' | 'pro' | 'enterprise';
  timeRemaining: number | null;
  isInGracePeriod: boolean;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  isHost,
  currentTier,
  timeRemaining,
  isInGracePeriod,
}) => {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'pro' | 'enterprise'>('pro');

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      await onUpgrade(selectedTier);
      onClose();
    } catch (error) {
      console.error('Upgrade failed:', error);
      alert('Upgrade failed. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const tiers = [
    {
      name: 'Pro',
      value: 'pro' as const,
      price: '$19/mo',
      features: [
        'Unlimited meeting duration',
        'Up to 50 participants',
        '4 simultaneous screen shares',
        'HD video quality',
        'Priority support',
      ],
      color: 'primary',
      popular: true,
    },
    {
      name: 'Enterprise',
      value: 'enterprise' as const,
      price: '$99/mo',
      features: [
        'Everything in Pro',
        'Up to 100 participants',
        '8 simultaneous screen shares',
        'Custom branding',
        'Dedicated support',
        'Advanced analytics',
      ],
      color: 'secondary',
      popular: false,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-neutral-900 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-white/10">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-all text-neutral-400 hover:text-white"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <Crown className="text-yellow-500" size={32} />
            <h2 className="text-2xl font-bold text-white">
              {isHost ? 'Upgrade to Continue Meeting' : 'Waiting for Host to Upgrade'}
            </h2>
          </div>
          
          {isInGracePeriod && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="text-red-500" size={20} />
              <p className="text-sm text-red-300">
                <strong>Grace Period:</strong> You have {timeRemaining !== null ? Math.ceil(5 + timeRemaining) : 5} minutes to upgrade before the meeting ends
              </p>
            </div>
          )}
          
          {!isHost && (
            <p className="text-neutral-400 mt-2">
              The host is being prompted to upgrade. The meeting will continue once they complete the upgrade.
            </p>
          )}
        </div>

        {/* Content */}
        {isHost ? (
          <div className="p-6">
            <p className="text-neutral-300 mb-6">
              Your free 30-minute meeting time has ended. Upgrade now to continue your meeting and unlock premium features.
            </p>

            {/* Tier Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {tiers.map((tier) => (
                <button
                  key={tier.value}
                  onClick={() => setSelectedTier(tier.value)}
                  className={`relative p-6 rounded-xl border-2 transition-all text-left ${
                    selectedTier === tier.value
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-6 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    <span className="text-2xl font-bold text-white">{tier.price}</span>
                  </div>
                  
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-300">
                        <Zap size={16} className="text-accent-400 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-700 disabled:text-neutral-500 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {isUpgrading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Upgrading...
                  </>
                ) : (
                  <>
                    <Crown size={20} />
                    Upgrade to {selectedTier === 'pro' ? 'Pro' : 'Enterprise'}
                  </>
                )}
              </button>
              
              <button
                onClick={onClose}
                disabled={isUpgrading}
                className="px-6 py-3 rounded-lg border border-white/20 hover:bg-white/10 text-white transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            {/* Demo note */}
            <p className="text-xs text-neutral-500 mt-4 text-center">
              🧪 <strong>Demo Mode:</strong> This will upgrade the meeting instantly (no payment required)
            </p>
          </div>
        ) : (
          // Participant view
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="text-primary-400 animate-pulse" size={32} />
            </div>
            
            <h3 className="text-xl font-semibold text-white mb-2">
              Please Wait
            </h3>
            <p className="text-neutral-400 mb-6">
              The meeting host is being asked to upgrade to continue. You'll be able to rejoin once they complete the upgrade.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
              Waiting for host...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};